/**
 * 数据大屏 - 聚合统计接口
 *
 * 设计原则：绝不让前端拉全表数据后再聚合。
 * 后端需提供以下聚合端点，在数据库层完成 COUNT / SUM / GROUP BY / COUNT DISTINCT。
 *
 * 如果后端尚未实现这些聚合端点：
 *   - 计数类：使用 pageSize=1 读取 total（仅拉 1 行）
 *   - 求和/分组类：需后端提供聚合接口，前端无法替代
 *   - 去重类：需后端提供 count distinct，前端无法替代
 */

import { get } from '@/utils/http'

/** 安全包装：接口不存在时不抛异常，返回默认值 */
async function safeGet(url, params = {}) {
  try {
    return await get(url, params)
  } catch (e) {
    return null
  }
}

// ============================================================
//  广告记录聚合（建议后端实现为 /admin/adRecord/statistics）
//  参数：startTime, endTime
//  返回：{ recordCount, totalEcpm, activeUserCount }
//  说明：后端执行 SELECT COUNT(*), SUM(ecpm), COUNT(DISTINCT user_id)
//        一次查询替代前端三次拉全表，性能提升数百倍
// ============================================================
export async function getAdRecordStats(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/adRecord/statistics', { startTime, endTime })
  if (result && result.data) {
    return {
      recordCount: result.data.recordCount || result.data.count || 0,
      totalEcpm: result.data.totalEcpm || result.data.sum || 0,
      activeUserCount: result.data.activeUserCount || result.data.userCount || 0
    }
  }
  return { recordCount: 0, totalEcpm: 0, activeUserCount: 0 }
}

// ============================================================
//  首页 24 小时收益与曝光趋势（Taku 分小时报表）
//  GET /admin/dashboard/hourlyTrend?startTime=&endTime=
//  返回：[{ hour, estimateRevenue, impression }, ...]
// ============================================================
export async function getHourlyTrend(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/hourlyTrend', { startTime, endTime })
  if (result && result.data) {
    const list = Array.isArray(result.data.list) ? result.data.list
      : Array.isArray(result.data) ? result.data : []
    return list.map(item => ({
      hour: item.hour || '',
      estimateRevenue: Number(item.estimateRevenue) || 0,
      impression: Number(item.impression) || 0,
      request: Number(item.request) || 0,
      dau: Number(item.dau) || 0
    }))
  }
  return []
}

// ============================================================
//  广告记录按小时聚合（建议后端实现为 /admin/adRecord/hourlyRevenue）
//  参数：startTime, endTime
//  返回：[{ hour: "2026-06-18 09", total_ecpm: 123 }, ...]
//  说明：SELECT DATE_FORMAT(created_time,'%Y-%m-%d %H') AS hour, SUM(ecpm)
//        GROUP BY hour ORDER BY hour
// ============================================================
export async function getHourlyRevenue(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/adRecord/hourlyRevenue', { startTime, endTime })
  if (result && result.data) {
    const list = Array.isArray(result.data.list) ? result.data.list
      : Array.isArray(result.data) ? result.data : []
    return list
  }
  return []
}

// ============================================================
//  广告记录按天聚合（建议后端实现为 /admin/adRecord/dailyRevenue）
//  参数：startTime, endTime
//  返回：[{ date: "2026-06-12", total_ecpm: 1234 }, ...]
//  说明：SELECT DATE(created_time) AS date, SUM(ecpm)
//        GROUP BY date ORDER BY date
// ============================================================
export async function getDailyRevenue(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/adRecord/dailyRevenue', { startTime, endTime })
  if (result && result.data) {
    const list = Array.isArray(result.data.list) ? result.data.list
      : Array.isArray(result.data) ? result.data : []
    return list
  }
  return []
}

// ============================================================
//  广告按平台汇总收益（/admin/adRecord/platformSummary）
//  参数：startTime, endTime
//  返回：{ totalRevenue, totalCount, items: [{ adnName, ecpm, percent }] }
//  说明：一次请求替代前端拉全表再 GROUP BY adnName + SUM
// ============================================================
export async function getAdPlatformSummary(startTime, endTime, extraParams = {}) {
  const result = await safeGet('/admin/adRecord/platformSummary', { startTime, endTime, ...extraParams })
  if (result && result.data) {
    return {
      totalRevenue: result.data.totalRevenue || 0,
      totalCount: result.data.totalCount || 0,
      items: Array.isArray(result.data.items) ? result.data.items : []
    }
  }
  return { totalRevenue: 0, totalCount: 0, items: [] }
}

// ============================================================
//  广告类型收益排行（首页：激励/开屏/信息流/横幅，不含插屏）
//  GET /admin/dashboard/adTypeRevenue?startTime=&endTime=
//  返回：[{ adType, ecpm, percent }, ...] 按收益降序
// ============================================================
export async function getAdTypeRevenue(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/adTypeRevenue', { startTime, endTime })
  if (result && result.data) {
    return Array.isArray(result.data) ? result.data : []
  }
  return []
}

// ============================================================
//  首页 Taku 核心指标概览
//  GET /admin/dashboard/takuOverview?startTime=&endTime=
//  返回：{ estimateRevenue, revenue, impression, request, fillRate, ecpm, dau }
// ============================================================
export async function getTakuOverview(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/takuOverview', { startTime, endTime })
  if (result && result.data) {
    return {
      estimateRevenue: Number(result.data.estimateRevenue) || 0,
      revenue: Number(result.data.revenue) || 0,
      impression: Number(result.data.impression) || 0,
      request: Number(result.data.request) || 0,
      fillRate: Number(result.data.fillRate) || 0,
      ecpm: Number(result.data.ecpm) || 0,
      dau: Number(result.data.dau) || 0
    }
  }
  return {
    estimateRevenue: 0,
    revenue: 0,
    impression: 0,
    request: 0,
    fillRate: 0,
    ecpm: 0,
    dau: 0
  }
}

// ============================================================
//  广告记录计数（仅计数，pageSize=1 只拉 1 行，用 total 字段）
//  参数：startTime, endTime
//  说明：即使后端只有分页接口，pageSize=1 + total 也比拉全表快几千倍
// ============================================================
export async function getAdRecordCount(startTime, endTime) {
  const result = await safeGet('/admin/adRecord/page', {
    page: 1,
    pageSize: 1,
    startTime,
    endTime
  })
  if (result && result.data) {
    return result.data.total || 0
  }
  return 0
}

// ============================================================
//  用户统计（建议后端实现为 /admin/users/statistics）
//  返回：{ totalUsers, teamLeaderCount }
//  说明：totalUsers 可直接从分页接口 total 获取
//        teamLeaderCount 需要 SELECT COUNT(*) WHERE role != 0
// ============================================================
export async function getUserStats() {
  const result = await safeGet('/admin/dashboard/users/statistics')
  if (result && result.data) {
    return {
      totalUsers: result.data.totalUsers || result.data.userCount || 0,
      teamLeaderCount: result.data.teamLeaderCount || result.data.leaderCount || 0
    }
  }
  return { totalUsers: 0, teamLeaderCount: 0 }
}

// ============================================================
//  提现统计（建议后端实现为 /admin/userWithdrawalRecords/statistics）
//  参数：status=1（已打款）
//        date（今日日期，可选）
//  返回：{ totalAmount, todayAmount }
//  说明：SELECT SUM(amount) FROM withdrawal WHERE status=1 [AND date=xxx]
//        pageSize=99999 拉全表绝对不可取！
// ============================================================
export async function getWithdrawalStats(date) {
  const params = { status: 1 }
  if (date) params.date = date
  const result = await safeGet('/admin/dashboard/userWithdrawalRecords/statistics', params)
  if (result && result.data) {
    return {
      totalAmount: parseFloat(result.data.totalAmount || result.data.total || 0),
      todayAmount: parseFloat(result.data.todayAmount || result.data.today || 0)
    }
  }
  return { totalAmount: 0, todayAmount: 0 }
}

// ============================================================
//  用户总数（简单的 total 读取，只拉 1 行）
// ============================================================
export async function getTotalUserCount() {
  const result = await safeGet('/admin/users', { page: 1, pageSize: 1 })
  if (result && result.data) {
    return result.data.total || 0
  }
  return 0
}

// ============================================================
//  首页风控中心
//  GET /admin/dashboard/riskCenter?limit=20&startTime=&endTime=
//  返回：{ riskDeviceCount, warnDeviceCount, aliyunDetectCount, totalUsers, riskRatio, typeStats, records }
// ============================================================
export async function getRiskCenter(limit = 20, startTime, endTime) {
  const result = await safeGet('/admin/dashboard/riskCenter', { limit, startTime, endTime })
  if (result && result.data) {
    return {
      riskDeviceCount: Number(result.data.riskDeviceCount) || 0,
      warnDeviceCount: Number(result.data.warnDeviceCount) || 0,
      aliyunDetectCount: Number(result.data.aliyunDetectCount) || 0,
      totalUsers: Number(result.data.totalUsers) || 0,
      riskRatio: Number(result.data.riskRatio) || 0,
      typeStats: Array.isArray(result.data.typeStats) ? result.data.typeStats : [],
      records: Array.isArray(result.data.records) ? result.data.records : []
    }
  }
  return {
    riskDeviceCount: 0,
    warnDeviceCount: 0,
    aliyunDetectCount: 0,
    totalUsers: 0,
    riskRatio: 0,
    typeStats: [],
    records: []
  }
}

// ============================================================
//  广告平台列表（GET /admin/adRecord/platforms）
//  后端执行 SELECT DISTINCT adn_name FROM ad_record
//  返回：["pangle", "admob", "gdt", ...]
// ============================================================
export async function getAdPlatforms() {
  const result = await safeGet('/admin/adRecord/platforms')
  if (result && result.data) {
    return Array.isArray(result.data) ? result.data : []
  }
  return []
}

// ============================================================
//  用户省份分布（数据大屏地图）
//  GET /admin/dashboard/userLocations?startTime=&endTime=
//  返回：[{ province: "广东", userCount: 12, revenue: 1234 }, ...]
//  revenue 为 ecpm（1元=1000），与前端 formatRevenue 一致
// ============================================================
export async function getUserLocations(startTime, endTime) {
  const result = await safeGet('/admin/dashboard/userLocations', { startTime, endTime })
  if (result && result.data) {
    const list = Array.isArray(result.data) ? result.data : []
    return list.map(item => ({
      province: item.province || '',
      userCount: Number(item.userCount) || 0,
      revenue: Number(item.revenue) || 0
    })).filter(item => item.province)
  }
  return []
}
